import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import '@fontsource-variable/fredoka';
import '@fontsource-variable/lexend';
import '@fontsource/patrick-hand';
import '@fontsource/kalam/700.css';
import '@fontsource/dancing-script';
import '@fontsource/pacifico';
import '@fontsource/satisfy';
import '@fontsource/homemade-apple';
import '@fontsource/gochi-hand';
import './art/art.css';
import './styles/global.css';
import './styles/i18n.css';
import './styles/onboarding.css';
import './styles/lesson.css';
import './styles/features.css';
import './styles/services.css';
import './styles/anim.css';
import './styles/parity.css';
import './styles/cards.css';
import './styles/power.css';
import { App } from './App';
import { loadCachedContent } from './lib/contentPack';
import { startCurriculumSync } from './lib/curriculum';
import { initContent } from './lib/content';
import { preloadThumbs } from './art/Thumb';
import { initSafeArea } from './lib/safeArea';
import { isNative } from './lib/platform';

// Notch side on iPhones in landscape (safe-area tokens), before the first paint.
initSafeArea();

// PWA service worker: web only. The native apps load their files from the APK / app bundle; an old worker there would
// keep serving the previous build after an update, so it is removed (one reload if it still controlled this page).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  if (!isNative) window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined));
  else
    void navigator.serviceWorker.getRegistrations().then(async (regs) => {
      if (!regs.length) return;
      await Promise.all(regs.map((r) => r.unregister()));
      if ('caches' in window) await caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))));
      if (navigator.serviceWorker.controller && !sessionStorage.getItem('sk-sw-gone')) {
        sessionStorage.setItem('sk-sw-gone', '1');
        location.reload();
      }
    });
}

// Thumbnail manifest of the default art style, fetched while the app boots (lists render images, not live figures).
preloadThumbs('A');

// Content shell (ids, titles, structure; world chunks load on demand — docs/PERF.md): a downloaded release (if any,
// validated) or the bundled one, before the first render.
void loadCachedContent()
  .catch(() => undefined)
  .then(() => initContent())
  // Curriculum v1 / v2 pack from the live-config flag (persisted config; re-applied when it changes).
  .then(() => startCurriculumSync())
  .then(() =>
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </StrictMode>,
    ),
  );
